import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import moment from "moment";
import "moment/locale/ru";
import style from "./ExpectedArrivalBlock.module.scss";
import { Expeditor } from "../../../../../services";
import CustomDatePicker from "../../../../shared/date-picker/Date";
import CalendarIcon from "../../../../shared/icons/CalendarIcon";
import ExpectedArrivalCalendar from "./ExpectedArrivalCalendar";
import { isExpectedArrivalRequired } from "./expectedArrivalValidation";

const SHOW_STATUSES = ["onthetrip"];

const ExpectedArrivalBlock = ({
  order,
  fetchOrderData,
  addressId,
  initialUnloadingDate,
  errorText = "",
  focusRequest = 0,
}) => {
  const { t } = useTranslation();
  const [date, setDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const blockRef = useRef(null);

  useEffect(() => {
    setDate(initialUnloadingDate ? new Date(initialUnloadingDate) : null);
  }, [initialUnloadingDate]);

  useEffect(() => {
    if (!focusRequest) return;
    blockRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    blockRef.current?.querySelector("input")?.focus({ preventScroll: true });
  }, [focusRequest]);

  const dateError = date ? "" : errorText;

  const status = (order?.status || "").toLowerCase();
  if (!SHOW_STATUSES.includes(status)) return null;

  const titleKey = isExpectedArrivalRequired({ status, companyId: order?.companyId })
    ? "orderInfoExpeditor.expectedArrival.requiredTitle"
    : "orderInfoExpeditor.expectedArrival.title";

  const saveDate = async (nextDate) => {
    if (isLoading) return;
    if (!nextDate || !addressId) {
      toast.error(t("orderFilter.datePlaceholder"));
      return;
    }
    const previousDate = date;
    setIsLoading(true);
    try {
      await Expeditor.updateExpectedUnloadingDate(addressId, { expectedUnloadingDate: nextDate.toISOString() });
      toast.success(t("orderInfoExpeditor.expectedArrival.dateUpdated"));
      if (fetchOrderData) await fetchOrderData();
    } catch {
      setDate(previousDate);
      toast.error(t("orderInfoExpeditor.expectedArrival.updateDateError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (nextDate) => {
    if (isLoading) return;
    setDate(nextDate);
    setIsCalendarOpen(false);
    if (nextDate) saveDate(nextDate);
  };

  const handleDateFieldClick = (event) => {
    if (isLoading || event.target.closest(".react-datepicker")) return;
    setIsCalendarOpen(true);
  };

  if (initialUnloadingDate) {
    return (
      <div ref={blockRef} className={style.card}>
        <div className={style.topbar}>
          <span className={style.topbar_title}>{t(titleKey)}</span>
        </div>

        <div className={style.view_row}>
          <div className={style.view_date}>
            <CalendarIcon />
            <span>{moment(initialUnloadingDate).locale("ru").format("D MMMM YYYY")}</span>
          </div>
          <ExpectedArrivalCalendar
            selected={date}
            onChange={setDate}
            onClear={() => setDate(null)}
            onSelect={() => saveDate(date)}
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <div ref={blockRef} className={style.card}>
      <div className={style.topbar}>
        <span className={style.topbar_title}>{t(titleKey)}</span>
      </div>

      <div
        className={cn(style.date_field, { [style.date_field_error]: dateError })}
        onClick={handleDateFieldClick}
      >
        <span className={cn(style.date_label, { [style.date_label_error]: dateError })}>
          {t("orderFilter.datePlaceholder")}
        </span>
        <CustomDatePicker
          selected={date}
          placeholderText={t("orderFilter.datePlaceholder")}
          dateFormat="dd.MM.yyyy"
          onChange={handleDateChange}
          disabled={isLoading}
          errorText={dateError}
          ariaInvalid={Boolean(dateError)}
          open={isCalendarOpen}
          onClickOutside={() => setIsCalendarOpen(false)}
          popperProps={{ strategy: "fixed" }}
        />
        <CalendarIcon />
      </div>
    </div>
  );
};

export default ExpectedArrivalBlock;
